/**
 * Anonymous public theme loader — used by /j/:slug apply pages and
 * /b/... embed boards. Fetches the company's public theme with a section
 * pre-merged (server-side) and applies the resulting CSS variables to
 * <html> so any Tailwind utilities keyed on --theme-* pick up the brand
 * automatically.
 *
 * The recruiter portal path uses BrandingContext.useSectionTheme — this hook
 * exists specifically for pages that DON'T have an authenticated user (and
 * therefore no BrandingProvider mounted with a base theme).
 */
import { useEffect } from "react";
import { api } from "@/lib/api";

const SYSTEM_FONTS = new Set([
    "Inter", "system-ui", "sans-serif", "serif", "monospace",
    "Arial", "Helvetica", "Georgia", "Times", "Courier",
]);

// Mirrors BrandingContext.applyTheme but as a stand-alone function so we
// don't need to import the whole context (public pages don't wrap in it).
function applyPublicTheme(theme) {
    if (!theme) return;
    const root = document.documentElement;
    const SET = (k, v) => {
        if (v !== undefined && v !== null && v !== "") root.style.setProperty(k, v);
    };
    SET("--theme-primary", theme.primary_color);
    SET("--theme-accent", theme.accent_color);
    SET("--theme-text", theme.text_color);
    SET("--theme-bg", theme.bg_color);
    SET("--theme-card", theme.card_bg);
    SET("--theme-muted", theme.muted_color);
    SET("--theme-border", theme.border_color);
    SET("--theme-link", theme.link_color || theme.primary_color);
    SET("--theme-success", theme.success_color);
    SET("--theme-warning", theme.warning_color);
    SET("--theme-danger", theme.danger_color);
    SET("--theme-input-bg", theme.input_bg);
    SET("--theme-header-bg", theme.header_bg || theme.card_bg);
    SET("--theme-body-font", theme.body_font);
    SET("--theme-heading-font", theme.heading_font || theme.body_font);
    SET("--theme-display-font", theme.display_font || theme.heading_font || theme.body_font);
    SET("--font-display", theme.display_font || theme.heading_font || theme.body_font);
    if (theme.base_font_size) SET("--theme-base-font-size", `${theme.base_font_size}px`);
    if (theme.h1_scale) SET("--theme-h1-size", `${theme.h1_scale}em`);
    if (theme.h2_scale) SET("--theme-h2-size", `${theme.h2_scale}em`);
    if (theme.line_height) SET("--theme-line-height", String(theme.line_height));
    if (theme.letter_spacing) {
        const ls = { tight: "-0.01em", normal: "0", wide: "0.02em" }[theme.letter_spacing] || "0";
        SET("--theme-letter-spacing", ls);
    }
    if (theme.card_radius !== undefined && theme.card_radius !== null) SET("--theme-card-radius", `${theme.card_radius}px`);
    if (theme.input_radius !== undefined && theme.input_radius !== null) SET("--theme-input-radius", `${theme.input_radius}px`);
    // Inject Google Fonts <link> for non-system body/heading families.
    for (const fam of [theme.body_font, theme.heading_font, theme.display_font]) {
        if (!fam) continue;
        const first = String(fam).split(",")[0].trim().replace(/^['"]|['"]$/g, "");
        if (!first || SYSTEM_FONTS.has(first)) continue;
        const id = `theme-font-${first.replace(/\s+/g, "-")}`;
        if (document.getElementById(id)) continue;
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(first).replace(/%20/g, "+")}:wght@300;400;500;600;700;800;900&display=swap`;
        document.head.appendChild(link);
    }
    if (theme.body_font) document.body.style.fontFamily = theme.body_font;
}

/**
 * Fetches `/api/public/companies/:cid/theme?section=<id>` and applies the
 * pre-merged theme to <html>. No-ops until ``companyId`` is available (the
 * public apply/embed pages fetch their job first, then get the company id).
 */
export function usePublicSectionTheme(companyId, sectionId) {
    useEffect(() => {
        if (!companyId || !sectionId) return () => {};
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/public/companies/${companyId}/theme`, {
                    params: { section: sectionId },
                });
                if (!cancelled && data?.theme) applyPublicTheme(data.theme);
            } catch { /* silent — no theme customisation, keep defaults */ }
        })();
        return () => { cancelled = true; };
    }, [companyId, sectionId]);
}
